import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import { getEnvVars } from "@/utils/EnvironmentUtils";
import axios, { AxiosInstance } from "axios";

/**
 * Dedicated axios instance for the signing API (apis/api, FastAPI — a different host than the
 * Spring backend). It is NOT an `HttpClientV2`: the signing API returns raw JSON, not the
 * Spring `ApiResultDTO` wrapper that `ApiResponseV2.fromAxiosResponse` expects.
 *
 * Headers:
 * - `Authorization: Bearer <token>` — attached per-request below (same token as HttpClientV2).
 * - `X-Tenant-Ext-Id` — attached by the interceptor that `TenantContext` registers via
 *   {@link registerSigningTenantInterceptor}, in the same effect that handles the default client,
 *   so both clients always agree on the active tenant.
 */

const REQUEST_TIMEOUT = 50000;

let client: AxiosInstance | null = null;

/** The signing API host for the current environment, or empty string when not configured. */
export function getSigningApiURL(): string {
  return (getEnvVars() as { signingApiURL?: string })?.signingApiURL ?? "";
}

/** True when the current environment has a signing API host configured. */
export function isSigningApiConfigured(): boolean {
  return getSigningApiURL().trim().length > 0;
}

export function getSigningClient(): AxiosInstance {
  if (!client) {
    client = axios.create({
      baseURL: getSigningApiURL(),
      timeout: REQUEST_TIMEOUT,
    });

    client.interceptors.request.use((request) => {
      const token = AuthService.getToken();
      if (token) {
        request.headers.set("Authorization", `Bearer ${token}`);
      }
      return request;
    });
  }

  return client;
}

/**
 * Registers the `X-Tenant-Ext-Id` request interceptor on the signing client.
 * Returns the interceptor id so the caller (TenantContext) can eject it on tenant change,
 * mirroring the lifecycle of the default-client interceptor.
 */
export function registerSigningTenantInterceptor(tenantExternalId: string): number {
  return getSigningClient().interceptors.request.use((request) => {
    // Default to the active tenant, but DO NOT clobber a tenant a caller set explicitly on the
    // request. A DOCUMENT_SIGNING task is owned by the customer's tenant; when a Lazo-team admin
    // opens it from the portal the active tenant is the firm's (`lazoTenant0`), so the task-scoped
    // calls must pin the request's own tenant (a server-derived value from the task DTO, never user
    // input) — otherwise the tenant-scoped signing API returns 404. See DocumentSigningTask.
    if (!request.headers.has("X-Tenant-Ext-Id")) {
      request.headers.set("X-Tenant-Ext-Id", tenantExternalId);
    }
    return request;
  });
}

export function ejectSigningTenantInterceptor(interceptorId: number): void {
  getSigningClient().interceptors.request.eject(interceptorId);
}

/** Test-only: reset the singleton so tests can re-create the client with fresh interceptors. */
export function resetSigningClientForTests(): void {
  client = null;
}
